export interface ToneSettings {
  brightness: number;
  contrast: number;
  saturation: number;
  invert: boolean;
  levels?: LevelsSettings;
  curves?: ToneCurves;
  gradientMap?: GradientMapSettings;
  blackWhite?: BlackWhiteSettings;
}

export interface LevelsSettings {
  inputBlack: number;
  inputWhite: number;
  gamma: number;
  outputBlack: number;
  outputWhite: number;
}

export interface ToneCurvePoint {
  x: number;
  y: number;
}

export interface ToneCurves {
  rgb: readonly ToneCurvePoint[];
  red: readonly ToneCurvePoint[];
  green: readonly ToneCurvePoint[];
  blue: readonly ToneCurvePoint[];
}

export interface GradientMapStop {
  position: number;
  color: string;
}

export interface GradientMapSettings {
  enabled: boolean;
  reverse: boolean;
  stops: readonly GradientMapStop[];
}

export interface BlackWhiteSettings {
  enabled: boolean;
  red: number;
  yellow: number;
  green: number;
  cyan: number;
  blue: number;
  magenta: number;
  tint: boolean;
  tintColor: string;
}

export const DEFAULT_BLACK_WHITE: Readonly<BlackWhiteSettings> = {
  enabled: false,
  red: 40,
  yellow: 60,
  green: 40,
  cyan: 60,
  blue: 20,
  magenta: 80,
  tint: false,
  tintColor: '#b8864b',
};

const clamp = (value: number, minimum = 0, maximum = 255) => Math.max(minimum, Math.min(maximum, value));

function curveLut(points: readonly ToneCurvePoint[] | undefined): Uint8Array {
  const normalized = [
    { x: 0, y: 0 },
    ...(points ?? [])
      .map((point) => ({ x: clamp(point.x, 0, 1), y: clamp(point.y, 0, 1) }))
      .filter((point) => point.x > 0 && point.x < 1)
      .sort((left, right) => left.x - right.x),
    { x: 1, y: 1 },
  ].filter((point, index, values) => index === 0 || point.x > (values[index - 1]?.x ?? -1));
  const lut = new Uint8Array(256);
  let segment = 0;
  for (let index = 0; index < lut.length; index += 1) {
    const position = index / 255;
    while (segment < normalized.length - 2 && position > (normalized[segment + 1]?.x ?? 1)) segment += 1;
    const start = normalized[segment] ?? { x: 0, y: 0 };
    const end = normalized[segment + 1] ?? { x: 1, y: 1 };
    const span = Math.max(Number.EPSILON, end.x - start.x);
    const amount = clamp((position - start.x) / span, 0, 1);
    lut[index] = Math.round(clamp(start.y + (end.y - start.y) * amount, 0, 1) * 255);
  }
  return lut;
}

function parseColor(value: string): [number, number, number] {
  const match = /^#([\da-f]{6})$/i.exec(value);
  if (!match?.[1]) return [0, 0, 0];
  const number = Number.parseInt(match[1], 16);
  return [(number >> 16) & 255, (number >> 8) & 255, number & 255];
}

function gradientColor(settings: GradientMapSettings, luminance: number): [number, number, number] {
  const stops = settings.stops
    .map((stop) => ({ position: clamp(stop.position, 0, 1), color: parseColor(stop.color) }))
    .sort((left, right) => left.position - right.position);
  if (stops.length === 0) return [luminance * 255, luminance * 255, luminance * 255];
  const position = settings.reverse ? 1 - luminance : luminance;
  const first = stops[0];
  const last = stops.at(-1);
  if (!first || !last) return [0, 0, 0];
  if (position <= first.position) return first.color;
  if (position >= last.position) return last.color;
  const endIndex = stops.findIndex((stop) => stop.position >= position);
  const end = stops[Math.max(1, endIndex)] ?? last;
  const start = stops[Math.max(0, endIndex - 1)] ?? first;
  const amount = (position - start.position) / Math.max(Number.EPSILON, end.position - start.position);
  return start.color.map((channel, index) => channel + ((end.color[index] ?? channel) - channel) * amount) as [number, number, number];
}

function hueSectorWeight(r: number, g: number, b: number, settings: BlackWhiteSettings): number {
  const maximum = Math.max(r, g, b);
  const minimum = Math.min(r, g, b);
  const chroma = maximum - minimum;
  if (chroma <= Number.EPSILON) return 1;
  let hue = maximum === r
    ? ((g - b) / chroma) % 6
    : maximum === g
      ? (b - r) / chroma + 2
      : (r - g) / chroma + 4;
  hue = (hue * 60 + 360) % 360;
  const weights = [settings.red, settings.yellow, settings.green, settings.cyan, settings.blue, settings.magenta, settings.red];
  const sector = hue / 60;
  const start = Math.floor(sector);
  const amount = sector - start;
  return ((weights[start] ?? 100) + ((weights[start + 1] ?? 100) - (weights[start] ?? 100)) * amount) / 100;
}

function tintGray(gray: number, color: string): [number, number, number] {
  const [red, green, blue] = parseColor(color).map((channel) => channel / 255) as [number, number, number];
  const maximum = Math.max(red, green, blue);
  const minimum = Math.min(red, green, blue);
  const chroma = maximum - minimum;
  const tintLightness = (maximum + minimum) / 2;
  const saturation = chroma === 0 ? 0 : chroma / (1 - Math.abs(2 * tintLightness - 1));
  let hue = chroma === 0 ? 0 : maximum === red
    ? ((green - blue) / chroma) % 6
    : maximum === green
      ? (blue - red) / chroma + 2
      : (red - green) / chroma + 4;
  hue = (hue * 60 + 360) % 360;
  const lightness = clamp(gray) / 255;
  const colorChroma = (1 - Math.abs(2 * lightness - 1)) * saturation;
  const x = colorChroma * (1 - Math.abs((hue / 60) % 2 - 1));
  const offset = lightness - colorChroma / 2;
  const segment: [number, number, number] = hue < 60 ? [colorChroma, x, 0]
    : hue < 120 ? [x, colorChroma, 0]
      : hue < 180 ? [0, colorChroma, x]
        : hue < 240 ? [0, x, colorChroma]
          : hue < 300 ? [x, 0, colorChroma]
            : [colorChroma, 0, x];
  return segment.map((channel) => (channel + offset) * 255) as [number, number, number];
}

export function applyTonePixels(pixels: Uint8ClampedArray, settings: ToneSettings, strength = 1): void {
  const brightness = settings.brightness * 255;
  const contrast = Math.max(-0.99, Math.min(0.99, settings.contrast));
  const contrastFactor = (1 + contrast) / (1 - contrast);
  const saturation = 1 + settings.saturation;
  const mix = Math.max(0, Math.min(1, strength));
  const levels = settings.levels ?? {
    inputBlack: 0,
    inputWhite: 1,
    gamma: 1,
    outputBlack: 0,
    outputWhite: 1,
  };
  const inputSpan = Math.max(1 / 255, levels.inputWhite - levels.inputBlack);
  const gamma = clamp(levels.gamma, 0.1, 9.99);
  const applyLevels = (channel: number) => {
    const normalized = clamp((channel / 255 - levels.inputBlack) / inputSpan, 0, 1);
    const corrected = normalized ** (1 / gamma);
    return (levels.outputBlack + corrected * (levels.outputWhite - levels.outputBlack)) * 255;
  };
  const compositeCurve = curveLut(settings.curves?.rgb);
  const channelCurves = [
    curveLut(settings.curves?.red),
    curveLut(settings.curves?.green),
    curveLut(settings.curves?.blue),
  ];
  for (let index = 0; index < pixels.length; index += 4) {
    const originalR = pixels[index] ?? 0;
    const originalG = pixels[index + 1] ?? 0;
    const originalB = pixels[index + 2] ?? 0;
    let r = contrastFactor * (originalR - 128) + 128 + brightness;
    let g = contrastFactor * (originalG - 128) + 128 + brightness;
    let b = contrastFactor * (originalB - 128) + 128 + brightness;
    const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    r = luminance + (r - luminance) * saturation;
    g = luminance + (g - luminance) * saturation;
    b = luminance + (b - luminance) * saturation;
    if (settings.invert) {
      r = 255 - r;
      g = 255 - g;
      b = 255 - b;
    }
    r = applyLevels(r);
    g = applyLevels(g);
    b = applyLevels(b);
    const curved = [r, g, b].map((channel, channelIndex) => {
      const composite = compositeCurve[Math.round(clamp(channel))] ?? 0;
      return channelCurves[channelIndex]?.[composite] ?? composite;
    });
    r = curved[0] ?? r;
    g = curved[1] ?? g;
    b = curved[2] ?? b;
    if (settings.blackWhite?.enabled) {
      const minimum = Math.min(r, g, b);
      const chroma = Math.max(r, g, b) - minimum;
      const gray = clamp(minimum + chroma * hueSectorWeight(r, g, b, settings.blackWhite));
      [r, g, b] = settings.blackWhite.tint ? tintGray(gray, settings.blackWhite.tintColor) : [gray, gray, gray];
    }
    if (settings.gradientMap?.enabled) {
      const mapped = gradientColor(settings.gradientMap, clamp(0.2126 * r + 0.7152 * g + 0.0722 * b) / 255);
      [r, g, b] = mapped;
    }
    pixels[index] = Math.round(originalR + (r - originalR) * mix);
    pixels[index + 1] = Math.round(originalG + (g - originalG) * mix);
    pixels[index + 2] = Math.round(originalB + (b - originalB) * mix);
  }
}
